/**
 * Centralized keyboard shortcuts configuration for modals
 *
 * This module provides a single source of truth for all keyboard shortcuts
 * used in the modal system. It's a simple lookup table for display strings.
 */

import { usePlatform } from '@/utils/helpers';

/**
 * Cached platform meta key character.
 * Platform doesn't change during a browser session, so we compute once at module load.
 */
let cachedMetaChar: string | null = null;

function getMetaChar(): string {
  if (cachedMetaChar === null) {
    const { metaChar } = usePlatform();
    cachedMetaChar = metaChar;
  }
  return cachedMetaChar;
}

/**
 * Simple lookup table for keyboard shortcut display strings
 * Note: For platform-specific shortcuts, use getKeyboardShortcuts() instead
 */
export const KEYBOARD_SHORTCUTS = {
  // Primary actions - use getKeyboardShortcuts().SAVE for platform-aware version
  SAVE: '⌘↵', // Kept for backward compatibility, prefer getPlatformShortcuts().SAVE
  CANCEL: 'Esc',

  // Navigation
  FIND: '/',
  ARROW_UP: '↑',
  ARROW_DOWN: '↓',
  ARROW_LEFT: '←',
  ARROW_RIGHT: '→',

  // Toggles
  TOGGLE_ARCHIVED: 'a',
  TOGGLE_NONE: 'n',
  TOGGLE_SUBTASKS: 's',
  CLEAR_ALL: 'c',

  // Selection
  SELECT_ALL: 'Cmd/Ctrl+A',
  SELECT_ITEM: 'Enter',
  TOGGLE_ITEM: 'Space',

  // Scenario-specific
  NEW_SCENARIO: 'n',
  RECORD_SCENARIO: 'r',
  RENAME_SCENARIO: 'e',
  DELETE_SCENARIO: 'Del',
} as const;

/**
 * Get platform-aware keyboard shortcuts
 * This function returns shortcuts with the correct modifier key for the current platform
 */
export function getPlatformShortcuts() {
  const metaChar = getMetaChar();
  return {
    ...KEYBOARD_SHORTCUTS,
    SAVE: `${metaChar}+↵`,
    SELECT_ALL: `${metaChar}+A`,
  };
}

/**
 * Type for keyboard shortcut keys
 */
export type KeyboardShortcutKey = keyof typeof KEYBOARD_SHORTCUTS;

/**
 * Simple helper to format a label with its keyboard shortcut
 * @param label The base label text
 * @param shortcut The shortcut string
 * @returns Formatted string like "Label (Shortcut)"
 */
export function withShortcut(label: string, shortcut: string): string {
  return `${label} (${shortcut})`;
}

/**
 * Get a formatted label with shortcut from the lookup table
 * @param label The base label text
 * @param shortcutKey The key from KEYBOARD_SHORTCUTS
 * @returns Formatted string like "Label (Shortcut)"
 */
export function withShortcutKey(
  label: string,
  shortcutKey: KeyboardShortcutKey
): string {
  return withShortcut(label, KEYBOARD_SHORTCUTS[shortcutKey]);
}

/**
 * Hook to use keyboard shortcuts in components
 * Returns both static shortcuts and platform-aware shortcuts
 */
export function useKeyboardShortcuts() {
  const platformShortcuts = getPlatformShortcuts();

  return {
    shortcuts: KEYBOARD_SHORTCUTS,
    /** Platform-aware shortcuts (⌘ on Mac, Ctrl on Windows/Linux) */
    platformShortcuts,
    /** Get the save shortcut for the current platform */
    saveShortcut: platformShortcuts.SAVE,
    withShortcut,
    withShortcutKey,
  };
}
